import PageNationNextIcon from '@assets/images/common/icon/pagination/next.svg';
import PageNationPrevIcon from '@assets/images/common/icon/pagination/prev.svg';
import { Pagination } from '@src/api/types/pagination';
import React from 'react';

interface PaginationProps {
  pagination: Pagination;
  onPageChange: (page: number) => void;
}

const PaginationComponent: React.FC<PaginationProps> = ({ pagination, onPageChange }) => {
  const { current_page, last_page } = pagination;

  if (!last_page || last_page <= 1) {
    return <></>;
  }

  // PC用のページボタンを取得
  const getPageNumbers = () => {
    const pages = new Set<number | string>();

    // Add first 5 pages
    for (let i = 1; i <= Math.min(5, last_page); i++) {
      pages.add(i);
    }

    // Add current page and its surrounding pages
    for (let i = Math.max(1, current_page - 2); i <= Math.min(last_page, current_page + 2); i++) {
      pages.add(i);
    }

    // Add last 2 pages
    for (let i = Math.max(1, last_page - 1); i <= last_page; i++) {
      pages.add(i);
    }

    // Convert set to array and sort
    const sortedPages = Array.from(pages).sort((a, b) => {
      if (typeof a === 'string' || typeof b === 'string') {
        return 0; // Keep strings (ellipses) in place
      }
      return a - b;
    });

    // Insert ellipses where needed
    const finalPages = [];
    let lastPage = 0;
    for (const page of sortedPages) {
      if (typeof page === 'number' && page > lastPage + 1) {
        finalPages.push('...');
      }
      finalPages.push(page);
      if (typeof page === 'number') {
        lastPage = page;
      }
    }

    return finalPages;
  };

  // SP用のページボタンを取得
  const getMobilePageNumbers = () => {
    // ページ表示の条件
    // - 最初の1,2ページ目or最後の1,2ページの時： 3ページ目までと最初or最後のページを表示
    // - それ以外の真ん中にいる時： カレントと最初と最後のページを表示
    // 例：20ページ目まであるとしたら
    // P1     now 2 3 ... 20
    // P2     1 now 3 ... 20
    // P3~18  1 ... now ... 20
    // P19    1 ... 18 now 20
    // P20    1 ... 18 19 now

    if (last_page <= 6) {
      return Array.from({ length: last_page }, (_, i) => i + 1);
    }
    const pages = new Set<number | string>();
    pages.add(1);
    if (current_page <= 2) {
      pages.add(2);
      pages.add(3);
    } else if (current_page >= last_page - 1) {
      pages.add(last_page - 2);
      pages.add(last_page - 1);
    } else {
      pages.add(current_page);
    }
    pages.add(last_page);

    const sortedPages = Array.from(pages).sort((a, b) => {
      if (typeof a === 'string' || typeof b === 'string') {
        return 0;
      }
      return a - b;
    });

    const finalPages = [];
    let lastPage = 0;
    for (const page of sortedPages) {
      if (typeof page === 'number' && page > lastPage + 1) {
        finalPages.push('...');
      }
      finalPages.push(page);
      if (typeof page === 'number') {
        lastPage = page;
      }
    }
    return finalPages;
  };

  const pcPages = getPageNumbers();
  const mobilePages = getMobilePageNumbers();

  const renderPageButton = (page: number | string, index: number) => {
    if (typeof page === 'number') {
      return (
        <li
          key={index}
          className={`size-[32px] rounded-sm border border-solid border-gray-200 text-center ${page === current_page ? '!border-none' : ''}`}
        >
          <button
            onClick={current_page === page ? undefined : () => onPageChange(page)}
            disabled={current_page === page}
            className={`grid size-full h-full place-items-center text-sm transition-opacity ${
              page === current_page ? 'rounded-sm bg-primary-200 text-white' : 'hover:opacity-60'
            }`}
          >
            {page}
          </button>
        </li>
      );
    }
    return (
      <li
        key={index}
        className='grid place-items-center !border-none'
      >
        <span>…</span>
      </li>
    );
  };

  return (
    <ul
      className='flex gap-1 [&>li]:h-[32px] [&>li]:min-w-[32px] [&>li]:rounded-sm [&>li]:border [&>li]:border-solid [&>li]:border-gray-200'
      data-testid='pagination'
    >
      {pagination.prev_page_url && (
        <li>
          <button
            onClick={() => onPageChange(current_page - 1)}
            className='grid h-full place-items-center px-[11px] transition-opacity hover:opacity-60'
          >
            <img
              src={PageNationPrevIcon}
              alt='Previous'
            />
          </button>
        </li>
      )}
      {/* PC View */}
      <div className='flex gap-1 md:hidden'>
        {pcPages.map((page, index) => renderPageButton(page, index))}
      </div>
      {/* SP View */}
      <div className='hidden gap-2 md:flex'>
        {mobilePages.map((page, index) => renderPageButton(page, index))}
      </div>
      {pagination.next_page_url && (
        <li>
          <button
            onClick={() => onPageChange(current_page + 1)}
            className='grid h-full place-items-center px-[11px] transition-opacity hover:opacity-60'
          >
            <img
              src={PageNationNextIcon}
              alt='Next'
            />
          </button>
        </li>
      )}
    </ul>
  );
};

export default PaginationComponent;
